import InventoryReviewGate from './components/inventory/InventoryReviewGate';
import PickQueueBadge from './components/inventory/PickQueueBadge';
import InventoryPricingLive from './components/inventory/PricingLive';
import BusinessPickList from './components/inventory/PickList';
import { Component, useEffect, useMemo, useState } from 'react';
import {
  BrowserRouter as Router,
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button, FeedbackRoot, Page, PageHeader } from 'design';
import AppFrame from './shell/AppFrame';
import Dashboard from './components/Dashboard';
import BuylistCSVUploader from './components/BuylistCSVUploader';
import StreamReview from './components/StreamReview';
import StreamPerformanceDashboard from './components/StreamPerformanceDashboard';
import CardShows from './components/CardShows';
import JapanCards from './components/JapanCards';
import SupplierOrders from './components/supplier-orders/SupplierOrders';
import SlabVault from './components/SlabVault';
import BusinessSlabInventory from './components/BusinessSlabInventory';
import SlabSurprise from './components/SlabSurprise';
import AuctionStreamPrep from './components/AuctionStreamPrep';
import BusinessSlabStorage from './components/inventory/SlabStorage';
import AddCosts from './components/inventory/AddCosts';
import ShopifyOrders from './components/inventory/ShopifyOrders';
import ShopifyQueue from './components/inventory/ShopifyQueue';
import StockCatalogue from './components/stock-catalogue/StockCatalogue';
import WhatnotInventory from './components/inventory/WhatnotInventory';
import StorageLabels from './components/inventory/StorageLabels';
import MovementHistoryPage from './components/inventory/MovementHistory';
import ContainerTransfersPage from './components/inventory/ContainerTransfers';
import StockReviewPage from './components/inventory/StockReview';
import PriceReviewPage from './components/inventory/PriceReview';
import SlabVaultLegacy from './components/SlabVaultLegacy';
import SlabViewerPage from './components/slab-viewer/SlabViewerPage';
import SealedVault from './components/SealedVault';
import CardrushProposals from './components/CardrushProposals';
import CardrushTinder from './components/CardrushTinder';
import AdminPanel from './components/AdminPanel';
import FulfilmentHub from './components/FulfilmentHub';
import FulfilmentPickView from './components/FulfilmentPickView';
import StreamIntelligence, { SalesSearch } from './components/StreamIntelligence';
import CardCamLive from './components/CardCamLive';
import {
  ADMIN_EMAILS,
  DEFAULT_STAFF_PERMISSIONS,
  hasPermission,
  isAdminUser,
  normalizeEmail,
  normalizePermissions,
} from './utils/staffPermissions';
import { getStoredUser } from './utils/auth';
import {inventoryLoginPath,rememberInventoryReturn,readInventoryReturn,clearInventoryReturn} from './utils/inventoryNavigation.mjs';

// ─── Auth helpers ──────────────────────────────────────────────────────────────
const ALLOWED_DOMAINS = ['pokerand.net', 'randcards.com'];

function isAllowedEmail(email) {
  const e = String(email || '').toLowerCase().trim();
  return ALLOWED_DOMAINS.some((d) => e.endsWith(`@${d}`));
}

function hasValidSession() {
  const user = getStoredUser();
  return Boolean(user?.accessToken && isAllowedEmail(user?.email));
}

// ─── Error boundary ────────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <Page width="narrow">
          <div className="rc-empty" role="alert">
            <h2 className="rc-empty__title">This page stopped working</h2>
            <p className="rc-empty__body">Reload to try again. If it keeps happening, send the details below to whoever looks after the portal.</p>
            <div className="rc-row">
              <button type="button" className="rc-btn rc-btn--primary" onClick={() => window.location.reload()}>Reload page</button>
              <Link className="rc-btn" to="/dashboard">Go home</Link>
            </div>
            <details style={{ width: '100%' }}>
              <summary className="rc-muted" style={{ cursor: 'pointer' }}>Error details</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, lineHeight: '16px', color: 'var(--rc-text-2)', marginTop: 8 }}>{this.state.error.message}{'\n'}{this.state.error.stack}</pre>
            </details>
          </div>
        </Page>
      );
    }
    return this.props.children;
  }
}

// ─── Guards ────────────────────────────────────────────────────────────────────
function RequireAuth({ children }) {
  const location=useLocation();
  if (!hasValidSession()) return <Navigate to={inventoryLoginPath(location.pathname,location.search)} replace />;
  return children;
}

function RequireAdmin({ children }) {
  if (!hasValidSession()) return <Navigate to="/login" replace />;
  if (!isAdminUser(getStoredUser())) return <Navigate to="/dashboard" replace />;
  return children;
}

function RequirePermission({ permission, children }) {
  if (!hasValidSession()) return <Navigate to="/login" replace />;
  if (!hasPermission(getStoredUser(), permission)) return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeRedirect() {
  return <Navigate to={hasValidSession() ? '/dashboard' : '/login'} replace />;
}

// ─── Auth card (login / error screens) ────────────────────────────────────────
function AuthCard({ title, subtitle, action }) {
  return (
    <div className="rc-auth">
      <div className="rc-auth__card">
        <img src="/randcards-logo.png" alt="RandCards" className="rc-auth__logo" />
        <h1 className="rc-auth__title">{title}</h1>
        {subtitle && <p className="rc-auth__text">{subtitle}</p>}
        {action}
      </div>
    </div>
  );
}

// ─── Login page ────────────────────────────────────────────────────────────────
function LoginPage() {
  const location=useLocation();
  useEffect(()=>{rememberInventoryReturn(new URLSearchParams(location.search).get('next'),sessionStorage);},[location.search]);
  let authUrl = '/api/direct-auth?hd=pokerand.net';
  try {
    const lastCompanyEmail = String(localStorage.getItem('lastCompanyEmail') || '').toLowerCase().trim();
    if (isAllowedEmail(lastCompanyEmail)) {
      const domain = lastCompanyEmail.split('@')[1];
      authUrl = `/api/direct-auth?hd=${encodeURIComponent(domain)}&login_hint=${encodeURIComponent(lastCompanyEmail)}`;
    }
  } catch { /* ignore */ }

  return (
    <div className="rc-auth">
      <div className="rc-auth__card">
        <img src="/randcards-logo.png" alt="RandCards" className="rc-auth__logo" />
        <h1 className="rc-auth__title">Staff portal</h1>
        <p className="rc-auth__text">Sign in with your RandCards or PokeRand Google account.</p>
        <a href={authUrl} className="rc-btn rc-btn--primary rc-btn--block">
          <svg width="18" height="18" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.332 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107"/>
            <path d="M6.306 14.691l6.571 4.819C14.655 15.108 19.001 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00"/>
            <path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.302 0-9.815-3.555-11.285-8.355l-6.522 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50"/>
            <path d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.021 35.291 44 30 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2"/>
          </svg>
          Sign in with Google
        </a>
      </div>
    </div>
  );
}

// The plain black page shown for the moment the portal checks who is signed in.
function BlankPage() {
  return <div style={{ minHeight: '100dvh', background: 'var(--rc-bg)' }} />;
}

// ─── Login success page ────────────────────────────────────────────────────────
// Google sends people here after they sign in. Save the session and go straight to the page they
// wanted. There is no welcome screen: AppLayout checks the account and loads permissions before
// it shows anything, so nothing here needs to wait.
function LoginSuccessPage() {
  const destination=useMemo(()=>readInventoryReturn(sessionStorage),[]);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const email = params.get('email');
  const name = params.get('name');
  const picture = params.get('picture');
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');

  useEffect(() => {
    if (!email || !name || !accessToken) return;
    if (!isAllowedEmail(email)) return;

    localStorage.setItem('user', JSON.stringify({
      email, name, picture, accessToken, refreshToken,
      role: ADMIN_EMAILS.includes(normalizeEmail(email)) ? 'admin' : 'staff',
      permissions: DEFAULT_STAFF_PERMISSIONS,
    }));
    localStorage.setItem('lastCompanyEmail', String(email).toLowerCase());

    // Records the sign in (and sets up the staff record the first time). The request carries on
    // after the page changes.
    fetch('/api/admin/upsert-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({}),
    }).catch((err) => console.error('Failed to upsert login:', err));

    clearInventoryReturn(sessionStorage);
    navigate(destination, { replace: true });
  }, [accessToken, destination, email, name, navigate, picture, refreshToken]);

  if (email && !isAllowedEmail(email)) {
    return (
      <AuthCard title="This account can't sign in" subtitle="Use your RandCards or PokeRand Google account."
        action={<a href="/login" className="rc-btn rc-btn--block">Back to sign in</a>} />
    );
  }

  if (!email || !name || !accessToken) {
    return (
      <AuthCard title="Sign in didn't finish" subtitle="Google didn't send your account details. Sign in again to continue."
        action={<a href="/login" className="rc-btn rc-btn--block">Back to sign in</a>} />
    );
  }

  return <BlankPage />;
}

// ─── App layout ────────────────────────────────────────────────────────────────
function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const stored = getStoredUser();
    if (!stored || !isAllowedEmail(stored.email)) {
      localStorage.removeItem('user');
      navigate(inventoryLoginPath(window.location.pathname,window.location.search));
      setAuthChecked(true);
      return;
    }

    const initialUser = {
      ...stored,
      role: stored.role || (isAdminUser(stored) ? 'admin' : 'staff'),
      permissions: normalizePermissions(stored.permissions),
    };
    setUser(initialUser);

    fetch('/api/user', { headers: { Authorization: `Bearer ${stored.accessToken}` } })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((payload) => {
        if (!payload?.email || !isAllowedEmail(payload.email)) throw new Error('Invalid user');
        const next = {
          ...stored,
          email: payload.email,
          name: payload.name || stored.name,
          picture: payload.picture || stored.picture || '',
          role: payload.role || initialUser.role,
          permissions: normalizePermissions(payload.permissions, initialUser.permissions),
        };
        // Home page pins: present only when the account can store them (a list, or null when none
        // were ever saved). Absent means they stay on this computer for now.
        if ('homePins' in payload) next.homePins = payload.homePins;
        else delete next.homePins;
        localStorage.setItem('user', JSON.stringify(next));
        setUser(next);
        setAuthChecked(true);
      })
      .catch(() => {
        localStorage.removeItem('user');
        navigate(inventoryLoginPath(window.location.pathname,window.location.search));
        setAuthChecked(true);
      });
  }, [navigate]);

  // Block render until auth check completes — prevents dashboard flash for unauthenticated visitors
  if (!authChecked) {
    return <BlankPage />;
  }

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <AppFrame
      user={user}
      onLogout={handleLogout}
      renderBadge={(item) => (item.badge === 'picks' ? <PickQueueBadge user={user} /> : null)}
    >
      <InventoryReviewGate user={user} pathname={location.pathname}><Outlet /></InventoryReviewGate>
    </AppFrame>
  );
}

function NotFoundPage() {
  const location = useLocation();
  return (
    <Page width="narrow">
      <PageHeader title="This page doesn't exist" description={`Nothing lives at ${location.pathname}. It may have moved. Search for the page with Cmd or Ctrl and K, or start from home.`} />
      <div className="rc-row">
        <Button variant="primary" to="/dashboard" icon={<ArrowLeft size={18} aria-hidden="true" />}>Go home</Button>
      </div>
    </Page>
  );
}

// ─── Root app ──────────────────────────────────────────────────────────────────
function App() {
  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/success" element={<LoginSuccessPage />} />
          <Route path="/login-success" element={<LoginSuccessPage />} />

          <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/buylist-uploader" element={<RequirePermission permission="buylist_uploader"><BuylistCSVUploader /></RequirePermission>} />
            <Route path="/slab-surprise" element={<ErrorBoundary><SlabSurprise /></ErrorBoundary>} />
            <Route path="/auction-stream-prep" element={<ErrorBoundary><AuctionStreamPrep /></ErrorBoundary>} />
            <Route path="/stream-review" element={<RequirePermission permission="stream_review"><StreamReview /></RequirePermission>} />
            <Route path="/stream-performance" element={<RequirePermission permission="stream_performance"><StreamPerformanceDashboard /></RequirePermission>} />
            {/* Fulfilment and Card Shows removed from main nav */}
            <Route path="/japan-cards" element={<RequirePermission permission="japan_cards"><JapanCards /></RequirePermission>} />
            <Route path="/supplier-orders" element={<ErrorBoundary><SupplierOrders /></ErrorBoundary>} />
            <Route path="/cardrush" element={<RequirePermission permission="cardrush"><CardrushProposals /></RequirePermission>} />
            <Route path="/cardrush/tinder" element={<CardrushTinder />} />
            <Route path="/slab-vault" element={<RequirePermission permission="slab_vault"><ErrorBoundary><SlabVault /></ErrorBoundary></RequirePermission>} />
            <Route path="/browse-slabs" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><SlabViewerPage /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-viewer" element={<Navigate to="/browse-slabs" replace />} />
            <Route path="/slab-inventory" element={<RequirePermission permission="slab_vault"><ErrorBoundary><BusinessSlabInventory /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/browse" element={<Navigate to="/browse-slabs" replace />} />
            <Route path="/slab-inventory/pricing" element={<RequirePermission permission="slab_vault"><ErrorBoundary><InventoryPricingLive /></ErrorBoundary></RequirePermission>} />
            <Route path="/stock-inventory" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><StockCatalogue /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/whatnot" element={<RequirePermission permission="slab_vault"><ErrorBoundary><WhatnotInventory /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/shopify" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><ShopifyQueue /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/picks" element={<RequirePermission permission="inventory_picking"><ErrorBoundary><BusinessPickList /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/storage" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><BusinessSlabStorage /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/costs" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><AddCosts /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/history" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><MovementHistoryPage /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/transfers" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><ContainerTransfersPage /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/review" element={<RequirePermission permission="slab_vault"><ErrorBoundary><StockReviewPage /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/price-review" element={<ErrorBoundary><PriceReviewPage /></ErrorBoundary>} />
            <Route path="/slab-inventory/orders" element={<RequirePermission permission="slab_vault"><ErrorBoundary><ShopifyOrders /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-inventory/storage/labels" element={<RequirePermission permission="inventory_safes"><ErrorBoundary><StorageLabels /></ErrorBoundary></RequirePermission>} />
            <Route path="/slab-vault/legacy" element={<RequirePermission permission="slab_vault"><ErrorBoundary><SlabVaultLegacy /></ErrorBoundary></RequirePermission>} />
            <Route path="/sealed-vault" element={<RequirePermission permission="slab_vault"><ErrorBoundary><SealedVault /></ErrorBoundary></RequirePermission>} />
            <Route path="/stream-intelligence" element={<RequirePermission permission="stream_intelligence"><StreamIntelligence /></RequirePermission>} />
            <Route path="/sales-search" element={<RequirePermission permission="sales_search"><SalesSearch /></RequirePermission>} />
            <Route path="/cardcam-live" element={<CardCamLive />} />
            <Route path="/admin" element={<RequireAdmin><AdminPanel /></RequireAdmin>} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route path="/" element={<HomeRedirect />} />
        </Routes>
      </Router>
      <FeedbackRoot />
    </>
  );
}

export default App;
